import { useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import AppContent from './AppContent';
import AppHeader from './AppHeader';
import AppSidebar from './AppSidebar';
import { updateValue, setSelectedTitle } from '../services/common/common.slice';
import matchesMenuPath from '../shared/utils/menuPath';
import { isUserClient, unMaskPath } from '../shared/utils/utils';
import PATH from '../shared/constants/path.const';
import Loader from '../shared/components/Loader';
import { getColors } from '../theme';

const colors = getColors();

const fullScreenPaths = ['/robotics/', '/welcome', '/robotics/robot-selection'];

function AppLayout() {
  const isClient = isUserClient();

  const matchMobile = useMediaQuery('(max-width:800px)');
  const sidebarMenus = useSelector(({ auth }) => auth.modules);
  const isFullScreen = fullScreenPaths.some((a) => window.location.pathname.indexOf(a) > -1);
  const [open, setOpen] = useState(false);
  const [drawopen, setDrawOpen] = useState(true);
  const [matchMenu, setMatchMenu] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState('');
  const [flattenMenu, setFlattenMenu] = useState(null);
  const { pathname, search } = useLocation();
  const [logoutCheckStatus, setLogoutCheckStatus] = useState(false);
  const [openSubMenuId, setOpenSubMenuId] = useState(sessionStorage.getItem('subMenu1') || null);

  const dispatch = useDispatch();

  const getMatchedMenu = (childMenu, menu, level) => {
    let match = null;
    if (matchesMenuPath(childMenu.screenPath, unMaskPath(pathname + search))) {
      sessionStorage.setItem(`subMenu${level}`, childMenu?.menuId);
      return { childMenu, menu };
    }
    if (childMenu?.childMenu) {
      childMenu.childMenu.forEach((subMenu) => {
        if (match === null) {
          match = getMatchedMenu(subMenu, menu, level + 1);
          if (match) {
            sessionStorage.setItem(`subMenu${level}`, childMenu?.menuId);
            sessionStorage.setItem(`subMenu${level + 1}`, subMenu?.menuId);
          }
          return match;
        }
        return null;
      });
    }
    return match;
  };

  const isMatchMenuPath = () => {
    let matchChildMenu = null;
    sidebarMenus.forEach((menu) => {
      menu.menus.map((childMenu) => {
        matchChildMenu = getMatchedMenu(childMenu, menu, 1);
        if (matchChildMenu) {
          setMatchMenu(matchChildMenu);
        }
        return childMenu;
      });
    });
  };
  const toggleDrawer = () => {
    dispatch(
      updateValue({
        name: 'isSidebarDrawerOpen',
        val: !open,
      })
    );
    isMatchMenuPath();
    setOpen(!open);
    setDrawOpen(open);
  };

  const toggleDrawer2 = () => {
    dispatch(
      updateValue({
        name: 'isSidebarDrawerOpen',
        val: !open,
      })
    );
    isMatchMenuPath();
    setOpenSubMenuId(null);
    setOpen(!open);
    setDrawOpen(open);
  };

  const flattenMenus = useCallback((menus) => {
    const result = [];
    menus.forEach((menu) => {
      if (menu?.childMenu) {
        result.push(menu?.childMenu);
      }
      menu.menus ? result.push(menu?.menus) : result.push(menu);
      menu.menus &&
        menu.menus.forEach((subMenu) => {
          if (subMenu?.childMenu) {
            result.push(...flattenMenus(subMenu?.childMenu));
          }
        });
    });
    return result;
  }, []);

  useEffect(() => {
    if (pathname === '/') {
      Object.keys(sessionStorage).forEach((key) => {
        const match = key.match(/^subMenu(\d+)$/);
        if (match) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }, [pathname]);
  useEffect(() => {
    if (sidebarMenus.length > 0) {
      if (!flattenMenu) {
        const flattenedMenus = flattenMenus(sidebarMenus)?.flat();
        setFlattenMenu(flattenedMenus);
      }
      isMatchMenuPath();
    }
  }, [sidebarMenus, pathname, search]);

  useEffect(() => {
    if (flattenMenu) {
      const current = unMaskPath(pathname + search);
      const toPath = (route) => {
        const path = route.screenPath;
        return path?.startsWith('/') ? path : `/${path}`;
      };
      const currentPathname = current.split('?')[0];
      const stsPaths = [PATH.PRODUCT_DEVELOPMENT, PATH.CLIENT_STS];
      const currentProjectId = new URLSearchParams(current.split('?')[1]).get('projectId');
      const sameProjectEntry = (route) => {
        const [routePathname, routeSearch] = toPath(route).split('?');
        return (
          Boolean(currentProjectId) &&
          stsPaths.includes(currentPathname) &&
          stsPaths.includes(routePathname) &&
          new URLSearchParams(routeSearch).get('projectId') === currentProjectId
        );
      };
      const routeName = (
        flattenMenu.find((route) => toPath(route) === current) ||
        flattenMenu.find(sameProjectEntry) ||
        flattenMenu.find((route) => matchesMenuPath(toPath(route), current)) ||
        flattenMenu.find((route) => toPath(route) === currentPathname)
      )?.caption;
      setSelectedMenu(pathname === PATH.HOME ? '' : routeName);
    }
  }, [flattenMenu, pathname, search]);

  useEffect(() => {
    dispatch(setSelectedTitle(selectedMenu));
  }, [selectedMenu]);

  useEffect(() => {
    dispatch(
      updateValue({
        name: 'isSidebarDrawerOpen',
        val: matchMobile,
      })
    );
  }, []);

  return (
    <>
      {logoutCheckStatus && <Loader style={{ height: '100vh' }} overlay />}
      <Box height="100%" bgcolor={colors.colorShades.pureWhite}>
        {!isFullScreen && (
          <Box display="flex" height="100%" flexDirection="column">
            <AppHeader
              drawopen={drawopen}
              open={open}
              toggleDrawer={toggleDrawer2}
              selectedMenu={selectedMenu}
              setLogoutCheckStatus={setLogoutCheckStatus}
            />
            <Box display="flex" flex={1} gap={1} overflow="auto">
              {!isClient && (
                <AppSidebar
                  setDrawOpen={setDrawOpen}
                  drawopen={drawopen}
                  setOpen={setOpen}
                  open={open}
                  toggleDrawer={toggleDrawer}
                  matchMenu={matchMenu}
                  openSubMenuId={openSubMenuId}
                  setOpenSubMenuId={setOpenSubMenuId}
                />
              )}
              <AppContent drawopen={drawopen} selectedMenu={selectedMenu} />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export default AppLayout;
